import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { WorkspacePremium } from "@mui/icons-material";
import { CssBaseline, createTheme, ThemeProvider, Tooltip, useTheme, } from "@mui/material";
import { useMemo } from "react";
import goldBgImage from "../assets/gold_bg_3.png";
import goldMedalLarge from "../assets/gold_medal_2.png";
import goldMedalSmall from "../assets/gold_medal_small.png";
export { goldBgImage, goldMedalLarge, goldMedalSmall };
const GOLD_STATUS = 1;
// Gold color constants for use in components
export const goldColors = {
    primary: "#c9a227",
    primaryDark: "#a68523",
    text: "#5c4a3a",
    textLight: "#8b7355",
    background: "#fdf6e3",
    surface: "#fdf5e0",
    surfaceLight: "#fefbf2",
};
export const isGoldClub = (club) => {
    const currentYear = new Date().getFullYear();
    return club.clubStatus?.some((s) => s.status === GOLD_STATUS && s.year === currentYear);
};
export const getGoldClubYears = (club) => club.clubStatus
    .filter((s) => s.status === GOLD_STATUS)
    .map((s) => s.year)
    .sort((a, b) => a - b);
export const createGoldTheme = (baseTheme, backgroundImage) => createTheme({
    ...baseTheme,
    components: {
        ...baseTheme.components,
        MuiCssBaseline: {
            styleOverrides: {
                body: {
                    backgroundColor: goldColors.background,
                    ...(backgroundImage && {
                        backgroundImage: `url(${backgroundImage})`,
                        backgroundPosition: "center",
                        backgroundSize: "cover",
                        backgroundAttachment: "fixed",
                    }),
                },
            },
        },
        MuiButton: {
            ...baseTheme.components?.MuiButton,
            styleOverrides: {
                ...baseTheme.components?.MuiButton?.styleOverrides,
                contained: {
                    background: "linear-gradient(180deg, #f5d060 0%, #e8a830 30%, #d4902a 100%)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.3)",
                    color: "#fff",
                    "&:hover": {
                        background: "linear-gradient(180deg, #e8a830 0%, #d4902a 30%, #c07828 100%)",
                        boxShadow: "inset 0 1px 0 rgba(255,255,255,0.2)",
                    },
                },
                outlined: {
                    backgroundColor: goldColors.surface,
                    borderColor: goldColors.primary,
                    color: goldColors.textLight,
                    "&:hover": {
                        backgroundColor: goldColors.background,
                        borderColor: goldColors.primaryDark,
                    },
                },
            },
        },
        MuiCard: {
            ...baseTheme.components?.MuiCard,
            styleOverrides: {
                ...baseTheme.components?.MuiCard?.styleOverrides,
                root: {
                    backgroundColor: goldColors.surface,
                    borderTop: `3px solid ${goldColors.primary}`,
                },
            },
        },
        MuiPaper: {
            ...baseTheme.components?.MuiPaper,
            styleOverrides: {
                ...baseTheme.components?.MuiPaper?.styleOverrides,
                root: {
                    backgroundColor: goldColors.surface,
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    borderColor: goldColors.primary,
                },
            },
        },
    },
});
export const useGoldTheme = (backgroundImage) => {
    const baseTheme = useTheme();
    return useMemo(() => createGoldTheme(baseTheme, backgroundImage), [baseTheme, backgroundImage]);
};
export const GoldThemeProvider = ({ children, enabled, backgroundImage, }) => {
    const goldTheme = useGoldTheme(backgroundImage);
    if (!enabled) {
        return _jsx(_Fragment, { children: children });
    }
    return (_jsxs(ThemeProvider, { theme: goldTheme, children: [_jsx(CssBaseline, {}), children] }));
};
// Gold medal component for inline display next to club names
export const GoldMedal = ({ src, inline = true, size = "2.4em", }) => (_jsx(Tooltip, { title: "Guldklubb", children: _jsx("img", { src: src, alt: "Guldklubb", style: {
            height: size,
            marginLeft: inline ? "0.3em" : undefined,
            marginRight: inline ? undefined : "0.5em",
            verticalAlign: "middle",
        } }) }));
// Pre-configured medal components with bundled assets
export const GoldMedalIcon = ({ size = "small", inline = true, }) => (_jsx(GoldMedal, { src: size === "small" ? goldMedalSmall : goldMedalLarge, size: size === "small" ? "1.2em" : "2.4em", inline: inline }));
// Gold badge component using WorkspacePremium icon
export const GoldBadge = () => (_jsx(Tooltip, { title: "Guldklubb", children: _jsx(WorkspacePremium, { sx: {
            fontSize: "1em",
            marginLeft: "0.3em",
            marginBottom: "0.2em",
            verticalAlign: "middle",
            color: goldColors.primary,
        } }) }));
//# sourceMappingURL=goldTheme.js.map